













































































































import {
  defineComponent,
  ref,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import sl from '@/serviceLocator';
import AddTextEditor from './Editor/AddTextEditor.vue';
import TextInfoSet from './TextInfo/TextInfoSet.vue';
import AddPhotos from './Photos/AddPhotos.vue';
import ConnectDiscourse from './Discourse/ConnectDiscourse.vue';
import EpigraphyView from '@/views/Texts/EpigraphyView/index.vue';
import FinalPreview from '@/views/Texts/CollectionTexts/AddTexts/Preview/FinalPreview.vue';
import StepperButton from './components/StepperButton.vue';
import {
  AddTextEditorContent,
  AddTextInfo,
  TextPhoto,
  EpigraphyResponse,
  CreateTextTables,
  TextDiscourseRow,
  TextPhotoWithDetails,
  TextRow,
  EpigraphyLabelLink,
} from '@oare/types';
import {
  convertTablesToDiscourseUnits,
  convertTablesToUnits,
} from './utils/convertTablesToUnits';
import { createNewTextTables } from './utils/buildTables';
import { addDetailsToTextPhotos } from './utils/photos';

export default defineComponent({
  props: {
    collectionUuid: {
      type: String,
      required: true,
    },
    existingTextUuid: {
      type: String,
      required: false,
    },
  },
  beforeRouteLeave(_to, _from, next) {
    if (!this.isDirty) {
      next();
    } else {
      this.actions.showUnsavedChangesWarning(next);
    }
  },
  components: {
    AddTextEditor,
    TextInfoSet,
    AddPhotos,
    EpigraphyView,
    ConnectDiscourse,
    FinalPreview,
    StepperButton,
  },
  setup(props) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');
    const router = sl.get('router');

    const collectionName = ref('');
    const step = ref(1);
    const loading = ref(false);

    const stepOneComplete = ref(false);
    const stepTwoComplete = ref(false);
    const stepThreeComplete = ref(false);
    const stepFourComplete = ref(false);

    const next = () => (step.value += 1);
    const previous = () => (step.value -= 1);

    const isDirty = ref(true);

    const existingTextRow = ref<TextRow>();

    onMounted(async () => {
      loading.value = true;
      try {
        collectionName.value = (
          await server.getCollectionInfo(props.collectionUuid)
        ).name;

        if (props.existingTextUuid) {
          const alreadyHasEpigraphy = await server.hasEpigraphy(
            props.existingTextUuid
          );
          if (alreadyHasEpigraphy) {
            isDirty.value = false;
            router.replace({ name: '403' });
          }

          existingTextRow.value =
            (await server.getTextRowByUuid(props.existingTextUuid)) ||
            undefined;
        }
      } catch (err) {
        isDirty.value = false;
        actions.showErrorSnackbar(
          'Error loading collection name. Please try again.',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    const textInfo = ref<AddTextInfo>();
    const setTextInfo = (updatedTextInfo: AddTextInfo) => {
      textInfo.value = updatedTextInfo;
    };

    const photos = ref<TextPhoto[]>([]);
    const setPhotos = (updatedPhotos: TextPhoto[]) => {
      photos.value = updatedPhotos;
    };
    const photosWithName = ref<TextPhotoWithDetails[]>([]);
    const photoUrls = computed(() =>
      photosWithName.value.filter(photo => photo.url).map(photo => photo.url)
    );
    const epigraphyLabelLinks: ComputedRef<EpigraphyLabelLink[]> = computed(
      () =>
        photos.value.map(photo => ({
          link: photo.url || '',
          label: `${store.getters.user?.firstName} ${store.getters.user?.lastName}`,
          side: photo.side || null,
          view: photo.view || null,
        }))
    );

    const editorContent = ref<AddTextEditorContent>();
    const setEditorContent = (updatedEditorContent: AddTextEditorContent) => {
      editorContent.value = updatedEditorContent;
    };

    const temporaryLocalTables = ref<CreateTextTables>();
    const buildTables = async () => {
      if (textInfo.value && editorContent.value) {
        photosWithName.value = await addDetailsToTextPhotos(
          textInfo.value.excavationPrefix,
          textInfo.value.excavationNumber,
          textInfo.value.museumPrefix,
          textInfo.value.museumNumber,
          textInfo.value.publicationPrefix,
          textInfo.value.publicationNumber,
          photos.value
        );

        temporaryLocalTables.value = await createNewTextTables(
          textInfo.value,
          editorContent.value,
          persistentDiscourseStorage.value,
          photosWithName.value,
          props.collectionUuid,
          existingTextRow.value
        );

        temporaryLocalTables.value.discourses.forEach(discourse => {
          persistentDiscourseStorage.value[discourse.uuid] = {
            spellingUuid: discourse.spellingUuid,
            transcription: discourse.transcription,
          };
        });
      }
    };

    const epigraphyDetails: ComputedRef<EpigraphyResponse> = computed(() => {
      return {
        canWrite: false,
        text: {
          uuid: temporaryLocalTables.value
            ? temporaryLocalTables.value.text.uuid
            : '',
          type: 'logosyllabic',
          name:
            textInfo.value && textInfo.value.textName
              ? textInfo.value.textName
              : '',
          excavationPrefix:
            textInfo.value && textInfo.value.excavationPrefix
              ? textInfo.value.excavationPrefix
              : '',
          excavationNumber:
            textInfo.value && textInfo.value.excavationNumber
              ? textInfo.value.excavationNumber
              : '',
          museumPrefix:
            textInfo.value && textInfo.value.museumPrefix
              ? textInfo.value.museumPrefix
              : '',
          museumNumber:
            textInfo.value && textInfo.value.museumNumber
              ? textInfo.value.museumNumber
              : '',
          publicationPrefix:
            textInfo.value && textInfo.value.publicationPrefix
              ? textInfo.value.publicationPrefix
              : '',
          publicationNumber:
            textInfo.value && textInfo.value.publicationNumber
              ? textInfo.value.publicationNumber
              : '',
        },
        collection: {
          uuid: props.collectionUuid,
          name: collectionName.value,
        },
        cdliNum:
          textInfo.value && textInfo.value.cdliNum
            ? textInfo.value.cdliNum
            : '',
        units: temporaryLocalTables.value
          ? convertTablesToUnits(temporaryLocalTables.value)
          : [],
        color: '',
        colorMeaning: '',
        discourseUnits: temporaryLocalTables.value
          ? convertTablesToDiscourseUnits(
              temporaryLocalTables.value.epigraphies,
              temporaryLocalTables.value.discourses
            )
          : [],
        hasEpigraphy: true,
        zoteroData: [],
      };
    });

    const persistentDiscourseStorage = ref<{
      [uuid: string]: {
        spellingUuid: string | null;
        transcription: string | null;
      };
    }>({});
    const updateDiscourseRows = (discourses: TextDiscourseRow[]) => {
      if (temporaryLocalTables.value) {
        temporaryLocalTables.value = {
          ...temporaryLocalTables.value,
          discourses,
        };
        temporaryLocalTables.value.discourses.forEach(row => {
          persistentDiscourseStorage.value[row.uuid] = {
            spellingUuid: row.spellingUuid,
            transcription: row.transcription,
          };
        });
      }
    };

    const manuallySelectedDiscourses = ref<string[]>([]);
    const updateManualSelections = (discourseUuid: string) => {
      manuallySelectedDiscourses.value =
        manuallySelectedDiscourses.value.filter(uuid => uuid !== discourseUuid);
      manuallySelectedDiscourses.value.push(discourseUuid);
    };

    const createText = async () => {
      if (temporaryLocalTables.value) {
        try {
          isDirty.value = false;
          await server.createText(temporaryLocalTables.value);
          await Promise.all(
            photosWithName.value.map(photo => server.uploadImage(photo))
          );
        } catch (err) {
          actions.showErrorSnackbar(
            'Error creating text. Please try again',
            err as Error
          );
        }
      }
    };

    const pushToText = () => {
      if (temporaryLocalTables.value) {
        router.push(`/epigraphies/${temporaryLocalTables.value.text.uuid}`);
      }
    };

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    onBeforeMount(() => {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    });

    return {
      actions,
      collectionName,
      step,
      loading,
      setTextInfo,
      setEditorContent,
      setPhotos,
      photoUrls,
      epigraphyDetails,
      next,
      previous,
      temporaryLocalTables,
      updateDiscourseRows,
      stepOneComplete,
      stepTwoComplete,
      stepThreeComplete,
      buildTables,
      pushToText,
      createText,
      manuallySelectedDiscourses,
      updateManualSelections,
      isDirty,
      existingTextRow,
      epigraphyLabelLinks,
      stepFourComplete,
    };
  },
});
